import { errorHelpers, oauthHelpers, routerHelpers } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { updateEntities } from 'modules/common/actions';
import { schemas } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { normalize } from 'normalizr';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { amplitudeService } from 'services/amplitude';
import { routingService } from 'services/routing';
import { getAdjustedIntegrationState } from 'shared/components';
import {
    useConnectToAirwallex,
    useDisconnectFromAirwallex,
    useFinishConnectionToAirwallex,
} from 'shared/data/webApp/v1';
import { useGetWorkflows } from 'shared/data/webApp/v1';
import { getPath, getUrl, Path } from 'urlBuilder';

import { AdditionalServiceCardAirwallexProps } from './AdditionalServiceCardAirwallex.types';

export const useAdditionalServiceCardAirwallex = (company: AdditionalServiceCardAirwallexProps['company']) => {
    const dispatch = useDispatch();
    const { search } = useLocation();

    const [isLoading, setIsLoading] = useState(false);

    const { mutateAsync: disconnectFromAirwallex } = useDisconnectFromAirwallex();
    const { mutateAsync: connectToAirwallexAsync } = useConnectToAirwallex();
    const { mutateAsync: finishConnectionToAirwallexAsync } = useFinishConnectionToAirwallex();
    const { refetch: getWorkflows } = useGetWorkflows({ query: { companyId: company.id } }, { enabled: false });

    const code = routerHelpers.getParamAsString(search, 'code', '');
    const state = routerHelpers.getParamAsString(search, 'state', '');
    const templates = useSelector((state) => selectors.template.getTemplatesByCompanyId(state, company.id));
    const hasServerObjects = templates.some((template) => template.integrationCode !== null);

    useEffect(() => {
        if (code && state && !company.airwallexIntegration?.isConnected) {
            setIsLoading(true);
            finishConnectionToAirwallexAsync(
                {
                    body: {
                        companyId: company.id,
                        state,
                        code,
                    },
                },
                {
                    onSettled: () => {
                        routingService.replace(getPath(Path.companyInfo, company.id));
                        setIsLoading(false);
                    },
                }
            );
        }
    }, [
        dispatch,
        code,
        state,
        company.id,
        company.airwallexIntegration?.isConnected,
        finishConnectionToAirwallexAsync,
    ]);

    const onDisconnect = useCallback(async () => {
        if (company.integrationId) {
            setIsLoading(true);
            await disconnectFromAirwallex({ body: { companyId: company.id, integrationId: company.integrationId } });

            if (!hasServerObjects) {
                const { data } = await getWorkflows();
                const { entities } = normalize(data?.templates, [schemas.templateSchema]);

                dispatch(updateEntities(entities));
            }

            setIsLoading(false);
        } else {
            throw errorHelpers.invalidOperationError();
        }
    }, [company.id, company.integrationId, disconnectFromAirwallex, dispatch, getWorkflows, hasServerObjects]);

    const connectToAirwallex = useCallback(async () => {
        const redirectUrl = getUrl(Path.airwallexAuth);

        setIsLoading(true);

        const response = await connectToAirwallexAsync({
            body: {
                companyId: company.id,
                redirectUrl,
            },
        });

        const url = oauthHelpers.encodeOAuthState<{ companyId: string }>(response.authorizeUrl, {
            companyId: company.id,
        });

        routingService.navigateToExternalUrl(url);
    }, [company.id, connectToAirwallexAsync]);

    const onConnect = useCallback(() => {
        amplitudeService.sendData('org settings: initiate connection', {
            'connection type': 'airwallex',
        });

        connectToAirwallex();
    }, [connectToAirwallex]);

    const onReconnect = useCallback(() => {
        connectToAirwallex();
    }, [connectToAirwallex]);

    return {
        isLoading,
        onConnect,
        onReconnect,
        onDisconnect,
    };
};

export const useCompanyIntegrationStatus = (company: selectors.types.ExpandedCompany) => {
    const workflows = useSelector((state) => selectors.template.getIntegrationTemplatesByCompanyId(state, company.id));
    const integration = useSelector((state) => selectors.integration.getIntegrationByCompanyId(state, company.id));

    const { isConnected, isDisconnected, isConnecting, isConnectingFinalizing, isDisconnecting } =
        getAdjustedIntegrationState(selectors.integration.getExplainedIntegrationState(integration), workflows);

    return {
        isIntegrationConnected: isConnected,
        isIntegrationConnecting: isConnecting || isConnectingFinalizing || isDisconnecting,
        isIntegrationDisconnected: isDisconnected || isConnecting,
    };
};
